import { Injectable, signal } from '@angular/core';
import { RepositoryObjectService } from '@softline/core';
import { Observable } from 'rxjs';
import { StockPosting } from '../models/stock-posting';
import { StockPostingPatch } from '../models/stock-posting-patch';
import { StockPostingQuery } from '../models/stock-posting-query';
import { StockPostingRepository } from '../repository/stock-posting.repository';
import { StockPostingFinalize } from '../models/stock-posting-finalize';

@Injectable()
export class StockPostingService extends RepositoryObjectService<
  StockPosting,
  StockPosting,
  null,
  StockPostingQuery
> {
  override params = signal(null);

  constructor(protected override repository: StockPostingRepository) {
    super(repository);
  }

  protected override getValue(result: StockPosting): StockPosting;
  protected override getValue(
    result: StockPosting | null | undefined
  ): StockPosting | undefined;
  protected override getValue(
    result: StockPosting | null | undefined
  ): StockPosting | undefined {
    return result ?? undefined;
  }

  async editHeadText(text: string) {
    await this.patchHeader({ text });
  }

  async patchHeader(body: Partial<StockPostingPatch>): Promise<StockPosting> {
    const result = await this.repository.patchHeader(this.value()?.id ?? 0, body);
    this.value.set(result);
    return result;
  }

  override async create(body) {
    const result = (await this.repository.create(body, {
      id: this.value()?.id ?? 0,
    })) as StockPosting;
    this.value.set(result);
    return result;
  }

  async finalize(body: StockPostingFinalize): Promise<StockPosting> {
    return await this.repository.finalize(
      {
        id: this.value()?.id ?? 0,
      },
      body
    );
  }

  // Observable-based load method
  loadOnly$(): Observable<StockPosting | null> {
    return new Observable<StockPosting | null>((subscriber) => {
      this.repository
        .load()
        .then((data) => {
          subscriber.next(data);
          subscriber.complete();
        })
        .catch(() => {
          subscriber.next(null);
          subscriber.complete();
        });
    })
      .pipe
      // Optional: cache the result
      ();
  }
}
